import React, {memo, useEffect} from "react";
import {Navigate, useNavigate, useParams} from "react-router-dom";
import {CircularLoading} from "../../molecules/loading/CircularLoading";
import {
    useFetchKuchikomyuSettingCustomerByRestaurantId
} from "../../../hooks/kuchikomyu/useFetchKuchikomyuSettingCustomerByRestaurantId";
import {Box} from "@chakra-ui/react";
import {usePostKuchikomyuOverallScoreFormEn} from "../../../hooks/kuchikomyu/usePostKuchikomyuOverallScoreFormEn";
import {PostOverallScoreFormEn} from "../../molecules/form/PostOverallScoreFormEn";
import { customNoAuthAxios } from "../../../utils/customNoAuthAxios";
import { useWatch } from "react-hook-form";
import { useSwrNoAuth } from "../../../hooks/swr/useSwrNoAuth";

const LOG_TAG = "[Kuchikomyu/PostScoreEn]";
const DEBUG_PREVENT_REDIRECT = false;

export const PostKuchikomyuOverallScoreEn: React.FC = memo(() => {
    const params = useParams();
    const {restaurantId} = params;
    const {kuchikomyuSetting, isLoading} = useFetchKuchikomyuSettingCustomerByRestaurantId(restaurantId);

    useEffect(() => {
        console.log(LOG_TAG, "page init", { restaurantId, isLoading });
    }, [restaurantId, isLoading]);

    useEffect(() => {
        if (!kuchikomyuSetting) return;
        const uqRaw = (kuchikomyuSetting as any).unique_question;
        const uniqueQuestionNormalized = uqRaw === 1 || uqRaw === '1' || uqRaw === true;
        console.log(LOG_TAG, "loaded settings", {
            branch_score: kuchikomyuSetting.branch_score,
            en_url: kuchikomyuSetting.en_url,
            default_en_url: kuchikomyuSetting.default_en_url,
            unique_question_raw: uqRaw,
            unique_question_normalized: uniqueQuestionNormalized,
        });
    }, [kuchikomyuSetting]);

    if (isLoading) return <CircularLoading/>;
    if (!kuchikomyuSetting || !restaurantId) return null;

    return (
        <Box p={4}>
            <ScorePost
                restaurantId={restaurantId}
                branchScore={kuchikomyuSetting.branch_score}
                enUrl={kuchikomyuSetting.en_url}
                defaultEnUrl={kuchikomyuSetting.default_en_url}
                uniqueQuestion={
                    ((kuchikomyuSetting as any).unique_question === 1) ||
                    ((kuchikomyuSetting as any).unique_question === '1') ||
                    ((kuchikomyuSetting as any).unique_question === true)
                }
            />
        </Box>
    );
});

const ScorePost: React.FC<{
    restaurantId: string;
    branchScore: number;
    enUrl: string;
    defaultEnUrl: string;
    uniqueQuestion: boolean;
}> = memo(({restaurantId, branchScore, enUrl, defaultEnUrl, uniqueQuestion}) => {
    const navigate = useNavigate();
    const {handleSubmit, control, isSubmitting, errors} = usePostKuchikomyuOverallScoreFormEn();

    const watchedScore = useWatch({ control, name: "score" });
    useEffect(() => {
        const n = Number(watchedScore);
        const fin = Number.isFinite(n);
        const preview = fin
            ? (n > branchScore ? "en_url" : (!uniqueQuestion ? "unique_survey_en" : "default_en_url"))
            : undefined;
        console.log(LOG_TAG, "star changed", {
            watchedScore,
            asNumber: n,
            isFinite: fin,
            branchScore,
            compareOperator: "> (より大きい場合に en_url)",
            willGo: preview,
            enUrl,
            defaultEnUrl,
            uniqueQuestion,
        });
    }, [watchedScore, branchScore, enUrl, defaultEnUrl, uniqueQuestion]);
    const onSubmit = async (values: any) => {
        const scoreNum = Number(values?.score);

        // 1) 総合スコアの登録
        try {
            console.log(LOG_TAG, "POST overall-score start", {
                endpoint: "kuchikomyu/overall-score/customer/",
                payload: { restaurant_id: restaurantId, score: scoreNum },
            });
            await customNoAuthAxios.post(`kuchikomyu/overall-score/customer/`, {
                restaurant_id: restaurantId,
                score: scoreNum,
            });
            console.log(LOG_TAG, "POST overall-score done");
        } catch (error) {
            console.log(LOG_TAG, "POST overall-score error", error);
            if (!DEBUG_PREVENT_REDIRECT) navigate('/thanks');
            return;
        }

        // 2) 遷移先を事前決定
        const isHigh = Number.isFinite(scoreNum) && scoreNum > branchScore;
        const nextUrl = isHigh ? enUrl : (defaultEnUrl || '/thanks');
        try { sessionStorage.setItem('kuchikomyu:nextUrl', nextUrl); } catch {}

        // 3) 変更点: unique_question=0（false）かつ低評価のみ EN アンケートへ
        if (!uniqueQuestion && !isHigh) {
            navigate(`/kuchikomyu/survey/en/${restaurantId}`, { replace: true, state: { nextUrl } });
            return;
        }

        // 4) それ以外は即座に nextUrl へ
        const isHttpUrl = (u?: string) => !!u && /^https?:\/\//i.test(u);
        const isInternalPath = (u?: string) => !!u && u.startsWith('/');

        if (isHttpUrl(nextUrl)) {
            if (!DEBUG_PREVENT_REDIRECT) window.location.href = nextUrl!;
            return;
        }
        if (isInternalPath(nextUrl)) {
            if (!DEBUG_PREVENT_REDIRECT) navigate(nextUrl!, { replace: true });
            return;
        }
        if (!DEBUG_PREVENT_REDIRECT) navigate('/thanks', { replace: true });
    };

    return (
        <PostOverallScoreFormEn
            onSubmit={handleSubmit(onSubmit)}
            control={control}
            isSubmitting={isSubmitting}
            errors={errors}
        />
    );
});